import { XGridDefaultColumn } from '@AMIEWEB/Common/XGrid/cells/XGridDefaults';
import { renderLoadableCell } from '@AMIEWEB/Order/OrderDetails/PayPackages/OrderPayPackages/LoadableCell';
import { missingField } from 'app/constants';
import { formatIfFractional } from 'app/helpers/numberHelper';
import React from 'react';
import { ITypeAheadOption, ITypeAheadOptionGroup } from 'app/models/Orders/OrderDetails';
import { GridSpecialtyWrapper } from './GridSpecialtyWrapper';
import { GridSubSpecialtyWrapper } from './GridSubSpecialtyWrapper';
import { GridDisciplineWrapper } from './GridDisciplineWrapper';
import { getCurrentTargetMarginCell } from '@AMIEWEB/Order/OrderDetails/PayPackages/OrderPayPackages/utils';
import { IFacilityTargetMargin } from 'store/redux-store/facility-preferences/types';
import uuid from 'react-uuid';
import { XGridOverflowEditableCell } from '@AMIEWEB/Common/XGrid/cells/XGridOverflowEditableCell';
import { isObject } from 'lodash';
import _ from 'lodash';
import { validateFacilityTargetMarginRows } from 'store/redux-store/facility-preferences/utils';
import { IXGridBulkActionButton } from '@AMIEWEB/Common/XGrid/Toolbar/XGridToolbar';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import AddIcon from '@mui/icons-material/Add';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { WhiteTooltip } from '@AMIEWEB/Common';

const FacilityTargetMarginDefaultCell = {
  ...XGridDefaultColumn,
  flex: 0,
};

const enforceDecimal = true;

const addTooltipToColumns = (cols: any[]) => {
  return cols.map(col => ({
    ...col,
    renderCell: (params: any) => {
      const rowError = params.row?.error;
      const hasError = rowError && rowError[params.field];
      const errorMessage = hasError ? getErrorMessage(params.field) : '';

      const cellContent = col.renderCell ? col.renderCell(params) : params.value;

      if (hasError) {
        return (
          <WhiteTooltip title={errorMessage} placement="bottom">
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              {cellContent}
            </div>
          </WhiteTooltip>
        );
      }

      return cellContent;
    },
  }));
};
  // Helper function to generate error messages
  const getErrorMessage = (field: string): string => {
    const skillsetFields = ['discipline', 'specialty', 'subSpecialty'];
    if (skillsetFields.includes(field)) {
      return 'Duplicate skillset';
    }
    return 'Required';
  };

export const facilityTargetMarginGridColumns = t => addTooltipToColumns([
  {
    ...XGridDefaultColumn,
    field: 'discipline',
    headerName: t('Discipline'),
    editable: true,
    renderCell: params => XGridOverflowEditableCell()()({ ...params, value: params.row?.discipline ?? missingField }),
    renderEditCell: params => <GridDisciplineWrapper {...params} label={t('Discipline')} />,
  },
  {
    ...XGridDefaultColumn,
    field: 'specialty',
    headerName: t('Specialty'),
    editable: true,
    renderCell: params => XGridOverflowEditableCell()()({ ...params, value: params.row?.specialty ?? missingField }),
    renderEditCell: params => <GridSpecialtyWrapper {...params} label={t('Specialty')} />,
  },
  {
    ...XGridDefaultColumn,
    field: 'subSpecialty',
    headerName: t('SubSpecialty'),
    editable: true,
    renderCell: params => XGridOverflowEditableCell()()({ ...params, value: params.row?.subSpecialty ?? missingField }),
    renderEditCell: params => <GridSubSpecialtyWrapper {...params} label={t('SubSpecialty')} />,
  },
  {
    ...FacilityTargetMarginDefaultCell,
    field: 'targetMarginDefault',
    headerName: t('Target Margin - Default'),
    width: 150,
    editable: true,
    ...getCurrentTargetMarginCell(t),
  },
  {
    ...FacilityTargetMarginDefaultCell,
    field: 'targetMarginDayShift',
    headerName: t('Target Margin - Day'),
    width: 150,
    editable: true,
    ...getCurrentTargetMarginCell(t),
  },
  {
    ...FacilityTargetMarginDefaultCell,
    field: 'targetMarginNightShift',
    headerName: t('Target Margin - Night'),
    width: 150,
    editable: true,
    ...getCurrentTargetMarginCell(t),
  },
  {
    ...FacilityTargetMarginDefaultCell,
    field: 'targetMarginDayNightShift',
    headerName: t('Target Margin - Day/Night'),
    width: 150,
    editable: true,
    ...getCurrentTargetMarginCell(t),
  },
  {
    field: 'payRangeMinMaxPercentage',
    headerName: t('Pay Range Min/Max Percentage'),
    width: 150,
    editable: true,
    ...getCurrentTargetMarginCell(t),
  },
  {
    ...FacilityTargetMarginDefaultCell,
    field: 'defaultWeeklyPay',
    headerName: t('Default Weekly Pay'),
    width: 150,
    editable: true,
    // Prevent invalid values
    valueParser: value => {
      if (value === null || value === undefined || value === '') {
        return null;
      }

      const parsed = Number(value);
      return isNaN(parsed) ? null : parsed;
    },
    renderCell: params =>
      renderLoadableCell({
        renderComponent: formatIfFractional(params.value, 2, '$', enforceDecimal),
        params,
      }),
  },
]);

export type ApiTypeAheadOption = {
  object: { ID: number; Abbreviation?: string; Description?: string };
  label: string;
  disabled?: boolean;
  groupBy?: string;
};

export type CleanOption = {
  id: number;
  value: string;
  label: string;
  raw: ITypeAheadOption;
  disabled?: boolean;
  groupBy?: ITypeAheadOptionGroup;
};

export const normalizeDisciplineOptions = (apiOptions: ITypeAheadOption[]): CleanOption[] =>
  (apiOptions ?? []).map(o => ({
    id: o.object.ID,
    value: o.object.Abbreviation ?? String(o.object.ID),
    label: o.label ?? o.object.Description ?? '',
    raw: o,
    groupBy: o.groupBy,
  }));
export const getBlankRow = (facilityId: number): IFacilityTargetMargin => {
  const clientId = uuid();
  return { ...blankRow, facilityId, clientId };
};
const blankRow = {
  disciplineId: null,
  discipline: null,
  specialtyId: null,
  specialty: null,
  subSpecialtyId: null,
  subSpecialty: null,
  targetMarginDefault: null,
  targetMarginDayShift: null,
  targetMarginNightShift: null,
  targetMarginDayNightShift: null,
  payRangeMinMaxPercentage: null,
  defaultWeeklyPay: null,
  updateCustomTargetMargin: true,
  updateCustomPayPackage: true,
  mode: 0,
  error: {
    discipline: false,
    specialty: false,
    subSpecialty: false,
    targetMarginDefault: false,
    targetMarginDayShift: false,
    targetMarginNightShift: false,
    targetMarginDayNightShift: false,
    payRangeMinMaxPercentage: false,
    defaultWeeklyPay: false,
    hasError: false,
  },
};
export const defualtColumnVisibility = {
  discipline: true,
  specialty: true,
  subSpecialty: true,
  targetMarginDefault: true,
  targetMarginDayShift: true,
  targetMarginNightShift: true,
  targetMarginDayNightShift: true,
  payRangeMinMaxPercentage: true,
  defaultWeeklyPay: true,
};

export const createLeftActions = (
  t: any,
  isSaveDisabled: boolean,
  promiseInProgress: boolean,
  rowSelectionModel: (string | number)[],
  dispatch: any,
  facilityPreferenceActions: any,
  facilityDetail: any,
  isSaving: boolean,
  resetInProgress: boolean,
  isDirty: boolean,
  facilityId: number
): IXGridBulkActionButton[] => [
  // @ts-ignore
  {
    title: t('candidate.workExperience.workExperienceGrid.saveChanges'),
    Icon: () => (
      <Stack direction="row" spacing={1} alignItems="center">
        <CheckIcon />
        {isSaving && <CircularProgress size={14} />}
      </Stack>
    ),
    disabled: isSaveDisabled || promiseInProgress || !isDirty,
    onClick: () => {
      dispatch(facilityPreferenceActions.saveFacilityTargetMargin({ facilityId }));
    },
  },
  // @ts-ignore
  {
    title: t('candidate.workExperience.workExperienceGrid.cancelChanges'),
    Icon: ClearIcon,
    disabled: !isDirty,
    onClick: () => {
      dispatch(facilityPreferenceActions.getFacilityTargetMargins({ facilityId: facilityDetail?.facilityId }));
    },
  },
  // @ts-ignore
  {
    title: t('order.orderDetails.preferences.targetMargins.grid.reset'),
    Icon: () => (
      <Stack direction="row" spacing={1} alignItems="center">
        <ReplayRoundedIcon />
        {resetInProgress && <CircularProgress size={14} />}
      </Stack>
    ),
    disabled: rowSelectionModel.length === 0 || promiseInProgress,
    onClick: () => {
      dispatch(facilityPreferenceActions.resetFacilityTargetMargins({ selectedIds: rowSelectionModel as string[], facilityId }));
    },
  },
];

export const createRightActions = (
  t: any,
  facilityTargetMargins: IFacilityTargetMargin[],
  rowSelectionModel: (string | number)[],
  promiseInProgress: boolean,
  dispatch: any,
  facilityPreferenceActions: any,
  facilityDetail: any
): IXGridBulkActionButton[] => [
  // @ts-ignore
  {
    Icon: () => (
      <Stack direction="row" spacing={1} alignItems="center">
        <AddIcon />
      </Stack>
    ),
    disabled: promiseInProgress,
    onClick: () => {
      dispatch(facilityPreferenceActions.updateFacilityTargetMargin(getBlankRow(facilityDetail?.facilityId ?? 0)));
    },
  },
  // @ts-ignore
  {
    Icon: () => (
      <Stack direction="row" spacing={1} alignItems="center">
        <ContentCopyRoundedIcon />
      </Stack>
    ),
    disabled: rowSelectionModel.length === 0 || promiseInProgress,
    onClick: () => {
      const updatedRows = facilityTargetMargins.reduce((acc: IFacilityTargetMargin[], margin) => {
        acc.push(margin);
        if (rowSelectionModel.includes(margin.clientId)) {
          const copiedRow = { ...margin, clientId: uuid() };
          acc.push(copiedRow);
        }
        return acc;
      }, []);
      const errorValidatedTargetMargin = validateFacilityTargetMarginRows(updatedRows);
      dispatch(facilityPreferenceActions.setFacilityTargetMargins(errorValidatedTargetMargin));
    },
  },
  // @ts-ignore
  {
    Icon: DeleteOutlineOutlinedIcon,
    disabled: rowSelectionModel.length === 0 || promiseInProgress,
    onClick: () => {
      dispatch(facilityPreferenceActions.removeFacilityTargetMarginsById(rowSelectionModel as string[]));
    },
  },
];

export const gridSx = {
  '& .MuiDataGrid-cell.cell-error:not(.MuiDataGrid-cell--editing)': {
    position: 'relative',
    outline: '2px solid #d32f2f',
    backgroundColor: '#fff5f5',
    outlineOffset: '-2px',
    borderRadius: '4px',
  },
  '& .MuiDataGrid-cell--editing.cell-error': {
    outline: 'none',
    backgroundColor: 'inherit',
  },
  '& .MuiDataGrid-cell': {
    transition: 'all 0.15s ease-in-out',
  },
};;

export const handleCellKeyDown = (
  params: any,
  event: React.KeyboardEvent,
  columns: any[],
  facilityTargetMargins: IFacilityTargetMargin[],
  apiRef: any
) => {
  if (event.key !== 'Tab') {
    return;
  }
  event.preventDefault();
  const currentColumnIndex = columns.findIndex(col => col.field === params.field);

  if (event.shiftKey) {
    const prevColumnIndex = currentColumnIndex - 1;
    if (prevColumnIndex >= 0) {
      const prevColumn = columns[prevColumnIndex];
      try {
        apiRef.current.stopCellEditMode({ id: params.id, field: params.field });
      } catch (error) {
        // Cell may already be out of edit mode (e.g., dropdown closed), ignore
      }
      setTimeout(() => {
        apiRef.current.startCellEditMode({
          id: params.id,
          field: prevColumn.field,
        });
      }, 0);
    } else {
      const rowIndex = facilityTargetMargins.findIndex(row => row.clientId === params.id);
      if (rowIndex > 0) {
        const prevRowId = facilityTargetMargins[rowIndex - 1].clientId;
        const lastColumn = columns[columns.length - 1];
        try {
          apiRef.current.stopCellEditMode({ id: params.id, field: params.field });
        } catch (error) {
          // Cell may already be out of edit mode, ignore
        }
        setTimeout(() => {
          apiRef.current.startCellEditMode({
            id: prevRowId,
            field: lastColumn.field,
          });
        }, 0);
      }
    }
    return;
  }

  const nextColumnIndex = currentColumnIndex + 1;

  if (nextColumnIndex < columns.length) {
    const nextColumn = columns[nextColumnIndex];
    try {
      apiRef.current.stopCellEditMode({ id: params.id, field: params.field });
    } catch (error) {
      // Cell may already be out of edit mode (e.g., dropdown closed), ignore
    }
    setTimeout(() => {
      apiRef.current.startCellEditMode({
        id: params.id,
        field: nextColumn.field,
      });
    }, 0);
  } else {
    // Move to next row if at the last column
    const rowIndex = facilityTargetMargins.findIndex(row => row.clientId === params.id);
    if (rowIndex < facilityTargetMargins.length - 1) {
      const nextRowId = facilityTargetMargins[rowIndex + 1].clientId;
      try {
        apiRef.current.stopCellEditMode({ id: params.id, field: params.field });
      } catch (error) {
        // Cell may already be out of edit mode, ignore
      }
      setTimeout(() => {
        apiRef.current.startCellEditMode({
          id: nextRowId,
          field: columns[0].field,
        });
      }, 0);
    }
  }
};

export const createProcessRowUpdate = (
  facilityTargetMargins: IFacilityTargetMargin[],
  dispatch: any,
  apiRef: any,
  facilityPreferenceActions: any
) => {
  return (newRow: any, oldRow: any) => {
    const newDisciplineId = newRow?.discipline?.id;
    const isDisciplineChanged = isObject(newRow?.discipline);
    let updatedRow = { ...oldRow, ...newRow };

    // Discipline changed -> clear dependent fields
    if ( isDisciplineChanged) {
      updatedRow = {
        ...newRow,
        disciplineId: newDisciplineId ?? null,
        discipline: newRow?.discipline?.label ?? null,
        specialty: null,
        specialtyId: null,
        subSpecialty: null,
        subSpecialtyId: null,
      };
    }

    const newSpecialtyId = newRow?.specialty?.id;
    const isSpecialtyChanged = isObject(newRow?.specialty);
    // Specialty changed -> clear dependent fields
    if ( isSpecialtyChanged) {
      updatedRow = {
        ...newRow,
        specialty: newRow?.specialty?.label ?? null,
        specialtyId: newSpecialtyId ?? null,
        subSpecialty: null,
        subSpecialtyId: null,
      };
    }
    const newSubSpecialtyId = newRow?.subSpecialty?.id;
    const isSubSpecialtyChanged = isObject(newRow?.subSpecialty);
    // SubSpecialty changed -> clear dependent fields
    if (isSubSpecialtyChanged) {
      updatedRow = {
        ...newRow,
        subSpecialty: newRow?.subSpecialty?.label ?? null,
        subSpecialtyId: newSubSpecialtyId ?? null,
      };
    }
    const targetMargin = _.cloneDeep(facilityTargetMargins);
    const marginIndex = facilityTargetMargins.findIndex(margin => margin.clientId === updatedRow.clientId);
    if (marginIndex !== -1) {
      targetMargin[marginIndex] = updatedRow;
    } else {
      targetMargin.push(updatedRow);
    }
    const errorValidatedTargetMargin = validateFacilityTargetMarginRows(targetMargin);
    dispatch(facilityPreferenceActions.setFacilityTargetMargins(errorValidatedTargetMargin));
    const errorValidatedUpdatedRow = errorValidatedTargetMargin.find(
      margin => margin.clientId === updatedRow.clientId
    );
    apiRef.current.updateRows([errorValidatedUpdatedRow]);
    return errorValidatedUpdatedRow;
  };
};